/** Where a running CSS animation is in time, keyed by its element, pseudo-element and name. */
export interface AnimationPhase {
  target: Element
  pseudoElement: string | null
  name: string
  currentTime: CSSNumberish
}

const cssAnimationName = (animation: Animation): string | null =>
  'animationName' in animation ? (animation as CSSAnimation).animationName : null

const keyframeTarget = (animation: Animation) => {
  const effect = animation.effect as KeyframeEffect | null
  return effect && effect.target
    ? { target: effect.target, pseudoElement: effect.pseudoElement ?? null }
    : null
}

/**
 * The current time of every CSS animation running on `root` or inside it, to carry over a
 * DOM move such as a teleport, which restarts them. Empty where the engine has no
 * `Element.getAnimations` (Chrome before 84, Safari before 13.1).
 */
export function captureAnimationPhases(root: Element | null): AnimationPhase[] {
  if (!root || typeof root.getAnimations !== 'function') return []
  const phases: AnimationPhase[] = []
  for (const animation of root.getAnimations({ subtree: true })) {
    const name = cssAnimationName(animation)
    const keyed = keyframeTarget(animation)
    if (name === null || !keyed || animation.currentTime === null) continue
    phases.push({ ...keyed, name, currentTime: animation.currentTime })
  }
  return phases
}

/** Puts each animation `captureAnimationPhases` saw back at its time, once its element has moved. */
export function restoreAnimationPhases(phases: AnimationPhase[]): void {
  for (const phase of phases) {
    for (const animation of phase.target.getAnimations({ subtree: true })) {
      const keyed = keyframeTarget(animation)
      if (
        keyed?.target === phase.target &&
        keyed.pseudoElement === phase.pseudoElement &&
        cssAnimationName(animation) === phase.name
      ) {
        animation.currentTime = phase.currentTime
      }
    }
  }
}
